/**
 * Panel administrativo principal.
 *
 * Concentrador operativo para:
 * - cargar usuarios e historiales,
 * - generar PDFs,
 * - subir dietas y rutinas,
 * - publicar acompañamiento,
 * - y disparar notificaciones push.
 *
 * Archivo grande por responsabilidad histórica: conviene mantener comentarios
 * visibles para ubicar rápido cada sección funcional.
 */
import React, { useCallback, useEffect, useState } from "react";
import "./DashboardAdmin.scss";
import NavbarDA from "./NavbarDA";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  listAll,
} from "firebase/storage";
import { Alert, Modal, Form, Input, Button, Select, Table, Collapse } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Loader from "./Loader";
import { sendPushNotification } from "../notifications";
import { db } from "../firebase";
import moment from "moment";
import "moment/locale/es"; // para formatear fechas en español

const routineDays = [
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
  "domingo",
];

const routineDayLabels = {
  lunes: "Lunes",
  martes: "Martes",
  miercoles: "Miércoles",
  jueves: "Jueves",
  viernes: "Viernes",
  sabado: "Sábado",
  domingo: "Domingo",
};

const routineDayAliases = {
  miercoles: ["miercoles", "miércoles"],
  sabado: ["sabado", "sábado"],
};

const NOTIFICATION_DASHBOARD_URL = "https://mentfull.com/dashboard";
const NOTIFICATION_ICON_URL = "https://mentfull.com/mentfull_ico.png";

const DashboardAdmin = () => {
  const [loading, setLoading] = useState(true);

  // Datos de usuarios e historiales
  const [users, setUsers] = useState([]);
  const [clinicalHistories, setClinicalHistories] = useState([]);

  // Vista actual (home, dietas, rutinas, notificaciones, acompañamiento)
  const [view, setView] = useState("home");

  // Estados para dietas
  const [selectedUser, setSelectedUser] = useState("");
  const [pdfFile, setPdfFile] = useState(null);

  // Estados para rutinas
  const [routineData, setRoutineData] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [exerciseForm] = Form.useForm();
  const [videoOptions, setVideoOptions] = useState([]);
  const [predefinedRoutines, setPredefinedRoutines] = useState([]);
  const [selectedPredefinedRoutine, setSelectedPredefinedRoutine] =
    useState("");
  const [isPresetNameModalVisible, setIsPresetNameModalVisible] =
    useState(false);
  const [presetRoutineName, setPresetRoutineName] = useState("");
  const [isSavingPresetRoutine, setIsSavingPresetRoutine] = useState(false);
  const [exerciseVideoFile, setExerciseVideoFile] = useState(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  // Estados para notificaciones
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationSubscriptions, setNotificationSubscriptions] = useState([]);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [notificationFeedback, setNotificationFeedback] = useState(null);
  // Lista de usuarios seleccionados para la notificación
  const [selectedUsersForNotification, setSelectedUsersForNotification] =
    useState([]);

  // Estados para la sección de Acompañamiento (posts)
  const [postTitle, setPostTitle] = useState("");
  const [postDescription, setPostDescription] = useState("");
  const [postHeader, setPostHeader] = useState(""); // Podría ser URL de imagen o video
  const [postCTA, setPostCTA] = useState(""); // Texto del botón
  const [postCTALink, setPostCTALink] = useState(""); // Link del botón
  const [posts, setPosts] = useState([]); // Lista de posteos existentes
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [postFeedback, setPostFeedback] = useState(null);

  const storage = getStorage();
  const { Panel } = Collapse;

  const refreshPosts = useCallback(async () => {
    const postsSnap = await getDocs(collection(db, "post"));
    const postsList = postsSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    postsList.sort((a, b) => {
      const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
      const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
      return dateB - dateA;
    });
    setPosts(postsList);
  }, []);

  const loadNotificationSubscriptions = useCallback(async () => {
    const subscriptionsSnap = await getDocs(collection(db, "suscripciones-fcm"));
    const subscriptionsList = subscriptionsSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    setNotificationSubscriptions(subscriptionsList);
  }, []);

  const loadExerciseVideos = useCallback(async () => {
    const videosRef = ref(storage, "exercise_videos");
    const videoList = await listAll(videosRef);
    const videoUrls = await Promise.all(
      videoList.items.map(async (item) => {
        const url = await getDownloadURL(item);
        return { name: item.name, url };
      })
    );
    videoUrls.sort((a, b) => a.name.localeCompare(b.name));
    setVideoOptions(videoUrls);
  }, [storage]);

  const loadPredefinedRoutines = useCallback(async () => {
    const predefinedRoutinesSnap = await getDocs(
      collection(db, "rutinas_preestablecidas")
    );
    const predefinedRoutinesList = predefinedRoutinesSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    predefinedRoutinesList.sort((a, b) => a.id.localeCompare(b.id));
    setPredefinedRoutines(predefinedRoutinesList);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Cargar usuarios
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);

        // 2. Cargar historiales clínicos
        const clinicalHistoriesSnapshot = await getDocs(
          collection(db, "clinicalHistory")
        );
        const clinicalHistoriesList = clinicalHistoriesSnapshot.docs.map(
          (doc) => ({
            id: doc.id,
            ...doc.data(),
          })
        );
        setClinicalHistories(clinicalHistoriesList);

        // 3. Cargar videos de Firebase Storage
        await loadExerciseVideos();

        // 4. Cargar rutinas predeterminadas, si la colección ya existe.
        await loadPredefinedRoutines();

        // 5. Cargar posteos y suscripciones de notificaciones.
        await refreshPosts();
        await loadNotificationSubscriptions();

        setLoading(false);
      } catch (error) {
        console.error("Error al cargar los datos:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [
    loadExerciseVideos,
    loadPredefinedRoutines,
    loadNotificationSubscriptions,
    refreshPosts,
  ]);

  // ============ GENERACIÓN DE PDF HISTORIAL ====================
  const generatePDF = (user, history) => {
    const docPdf = new jsPDF();
    const logo = new Image();
    logo.src = "/assets/images/logo-color.png";

    // Definir las secciones con las claves y el texto narrativo
    const sections = [
      {
        title: "Datos Complementarios",
        narrative:
          "Los siguientes datos corresponden a los datos otorgados por el paciente en la sección de Datos Complementarios:",
        fields: ["ocupacion", "estadoCivil", "objetivo"],
      },
      {
        title: "Antecedentes",
        narrative:
          "Los siguientes datos corresponden a la sección de Antecedentes:",
        fields: [
          "tabaco",
          "drogas",
          "alcohol",
          "cafe",
          "problemasPresion",
          "problemasCardio",
          "padecesEnfermedad",
          "detalleEnfermedades",
          "alergias",
          "suplementos",
          "medicamento",
          "dosis",
          "desdeCuando",
        ],
      },
      {
        title: "Indicadores",
        narrative:
          "Los siguientes datos corresponden a la sección de Indicadores:",
        fields: [
          "malestaresClinicos",
          "apetito",
          "horaHambre",
          "alimentosFavoritos",
          "alimentosMalestar",
          "alimentosNoTeGustan",
        ],
      },
      {
        title: "Lifestyle",
        narrative:
          "Los siguientes datos corresponden a la sección de Lifestyle:",
        fields: [
          "deportesPracticas",
          "diasSemanaPractica",
          "horasDiaPractica",
          "tiempoPractica",
          "desayuno",
          "colacionMatutina",
          "comida",
          "colacionVespertina",
          "cena",
          "horasDuermes",
          "litrosAgua",
          "estiloVidaSaludable",
          "habitoPerjudicial",
        ],
      },
      {
        title: "Medidas",
        narrative: "Los siguientes datos corresponden a la sección de Medidas:",
        fields: [
          "peso",
          "talla",
          "estatura",
          "brazo",
          "cintura",
          "pierna",
          "cadera",
          "fuerza",
          "resistencia",
          "movilidad",
          "flexibilidad",
        ],
      },
    ];

    // Mapeo de etiquetas legibles para cada campo
    const fieldLabels = {
      ocupacion: "Ocupación",
      estadoCivil: "Estado Civil",
      objetivo: "Objetivo",
      tabaco: "Consumo de Tabaco",
      drogas: "Consumo de Drogas",
      alcohol: "Consumo de Alcohol",
      cafe: "Consumo de Café",
      problemasPresion: "Problemas de Presión",
      problemasCardio: "Problemas Cardiovasculares",
      padecesEnfermedad: "Padeces alguna Enfermedad",
      detalleEnfermedades: "Detalles de Enfermedades",
      alergias: "Alergias",
      suplementos: "Suplementos o Medicamentos",
      medicamento: "Medicamento",
      dosis: "Dosis",
      desdeCuando: "Desde Cuándo lo Consumes",
      malestaresClinicos: "Malestares Clínicos",
      apetito: "Apetito",
      horaHambre: "Hora de Mayor Hambre",
      alimentosFavoritos: "Alimentos Favoritos",
      alimentosMalestar: "Alimentos que Causan Malestar",
      alimentosNoTeGustan: "Alimentos que no te Gustan",
      deportesPracticas: "Deportes que Practicas",
      diasSemanaPractica: "Días de Práctica",
      horasDiaPractica: "Horas de Práctica Diarias",
      tiempoPractica: "Tiempo Practicado",
      desayuno: "Desayuno",
      colacionMatutina: "Colación Matutina",
      comida: "Comida",
      colacionVespertina: "Colación Vespertina",
      cena: "Cena",
      horasDuermes: "Horas de Sueño",
      litrosAgua: "Litros de Agua Diarios",
      estiloVidaSaludable: "Estilo de Vida Saludable",
      habitoPerjudicial: "Hábito Perjudicial",
      peso: "Peso (kg)",
      talla: "Talla (Vestimenta)",
      estatura: "Estatura (m)",
      brazo: "Brazo (cm)",
      cintura: "Cintura (cm)",
      pierna: "Pierna (cm)",
      cadera: "Cadera (cm)",
      fuerza: "Fuerza",
      resistencia: "Resistencia",
      movilidad: "Movilidad",
      flexibilidad: "Flexibilidad",
    };

    logo.onload = function () {
      // Encabezado: logo y título
      docPdf.addImage(logo, "PNG", 10, 10, 57, 11);
      docPdf.setFontSize(22);
      docPdf.text("Historial Clínico", 70, 20);

      // Información básica del paciente
      docPdf.setFontSize(8);
      docPdf.text(`Nombre: ${user.name}`, 20, 30);
      docPdf.text(`Email: ${user.email}`, 20, 38);

      let currentY = 45;
      // Recorrer cada sección y generar narrativa + tabla
      sections.forEach((section) => {
        // Imprimir narrativa de la sección
        docPdf.setFontSize(8);
        docPdf.text(section.narrative, 20, currentY);
        currentY += 7;

        // Preparar datos para la sección
        const sectionData = section.fields.map((key) => {
          let value = history[key];
          // Convertir booleanos a "Sí"/"No"
          if (typeof value === "boolean") {
            value = value ? "Sí" : "No";
          }
          // Si es un array, unir sus elementos
          if (Array.isArray(value)) {
            value = value.join(", ") || "N/A";
          }
          // Si no hay valor, asignar "N/A"
          if (value === undefined || value === null || value === "") {
            value = "N/A";
          }
          return [fieldLabels[key] || key, value];
        });

        // Crear la tabla para la sección
        docPdf.autoTable({
          startY: currentY,
          head: [["Campo", "Valor"]],
          body: sectionData,
          theme: "grid",
          headStyles: { fillColor: [192, 211, 211], textColor: 111 },
          bodyStyles: { fillColor: [245, 245, 245] },
          styles: { fontSize: 8 },
          margin: { left: 20, right: 20 },
        });

        // Actualizar currentY para la siguiente sección
        currentY = docPdf.autoTable.previous.finalY + 10;
      });

      // Pie de página con fecha y hora de generación
      docPdf.setFontSize(8);
      docPdf.text(
        `Generado el: ${moment().format("DD/MM/YYYY HH:mm")}`,
        20,
        docPdf.internal.pageSize.getHeight() - 10
      );

      docPdf.save(`historial_clinico_${user.name}.pdf`);
    };
  };

  // ============ ENVÍO DE DIETA ====================
  const handleDietSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser || !pdfFile) {
      alert("Por favor, selecciona un usuario y un archivo PDF.");
      return;
    }

    try {
      const storageRef = ref(storage, `dietas/${selectedUser}/${pdfFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, pdfFile);

      uploadTask.on(
        "state_changed",
        null,
        (error) => {
          console.error("Error al subir el archivo:", error);
          alert("Error al subir la dieta.");
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const dietPlanDocRef = doc(collection(db, "dietPlans"), selectedUser);
          await setDoc(dietPlanDocRef, {
            fileName: pdfFile.name,
            pdfUrl: downloadURL,
          });
          alert("Dieta subida con éxito.");
        }
      );
    } catch (error) {
      console.error("Error al enviar la dieta:", error);
    }
  };

  // ============ ENVÍO DE RUTINA ====================
  const handleRoutineSubmit = async () => {
    if (!selectedUser) {
      alert("Selecciona un usuario antes de guardar la rutina.");
      return;
    }
    try {
      const routineDocRef = doc(collection(db, "rutines"), selectedUser);
      await setDoc(routineDocRef, { plan: routineData });
      alert("Rutina asignada con éxito.");
    } catch (error) {
      console.error("Error al asignar la rutina:", error);
    }
  };

  const handleExerciseVideoUpload = async () => {
    if (!exerciseVideoFile) {
      alert("Selecciona un video antes de subirlo.");
      return;
    }

    const videoName = exerciseVideoFile.name.replace(/\s+/g, "_");
    const storageRef = ref(storage, `exercise_videos/${Date.now()}_${videoName}`);
    const uploadTask = uploadBytesResumable(storageRef, exerciseVideoFile);

    setIsUploadingVideo(true);
    setVideoUploadProgress(0);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setVideoUploadProgress(progress);
      },
      (error) => {
        console.error("Error al subir el video:", error);
        setIsUploadingVideo(false);
        alert("No se pudo subir el video. Intenta de nuevo.");
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setVideoOptions((prev) =>
          [...prev, { name: uploadTask.snapshot.ref.name, url: downloadURL }]
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setExerciseVideoFile(null);
        setIsUploadingVideo(false);
        setVideoUploadProgress(100);
        alert("Video subido con éxito. Ya puedes seleccionarlo en ejercicios.");
      }
    );
  };

  const handleApplyPredefinedRoutine = () => {
    if (!selectedPredefinedRoutine) {
      alert("Selecciona una rutina predeterminada.");
      return;
    }

    const routine = predefinedRoutines.find(
      (item) => item.id === selectedPredefinedRoutine
    );
    const normalizedRoutine = normalizePredefinedRoutine(routine);

    if (!normalizedRoutine || Object.keys(normalizedRoutine).length === 0) {
      alert(
        "La rutina seleccionada no tiene ejercicios con el formato esperado."
      );
      return;
    }

    setRoutineData(normalizedRoutine);
    alert(
      "Rutina predeterminada agregada. Revisa los días y guarda para vincularla al usuario seleccionado."
    );
  };

  const showPresetNameModal = () => {
    if (Object.keys(routineData).length === 0) {
      alert("Agrega ejercicios antes de guardar una rutina preestablecida.");
      return;
    }

    setPresetRoutineName("");
    setIsPresetNameModalVisible(true);
  };

  const handleSavePredefinedRoutine = async () => {
    const routineId = presetRoutineName.trim().replace(/\//g, "-");

    if (!routineId) {
      alert("Escribe un nombre para la rutina preestablecida.");
      return;
    }

    if (Object.keys(routineData).length === 0) {
      alert("No hay ejercicios para guardar como rutina preestablecida.");
      return;
    }

    try {
      setIsSavingPresetRoutine(true);
      await setDoc(doc(db, "rutinas_preestablecidas", routineId), {
        plan: routineData,
      });
      await loadPredefinedRoutines();
      setSelectedPredefinedRoutine(routineId);
      setIsPresetNameModalVisible(false);
      setPresetRoutineName("");
      alert("Rutina preestablecida guardada con éxito.");
    } catch (error) {
      console.error("Error al guardar rutina preestablecida:", error);
      alert("No se pudo guardar la rutina preestablecida. Intenta de nuevo.");
    } finally {
      setIsSavingPresetRoutine(false);
    }
  };

  const showModal = (day) => {
    setSelectedDay(day);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    exerciseForm.validateFields().then((values) => {
      setRoutineData((prev) => ({
        ...prev,
        [selectedDay]: {
          ...(prev[selectedDay] || {}),
          [`ejercicio${Object.keys(prev[selectedDay] || {}).length + 1}`]:
            values,
        },
      }));
      setIsModalVisible(false);
      exerciseForm.resetFields();
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    exerciseForm.resetFields();
  };

  // ============ ENVÍO DE NOTIFICACIONES ====================
  const handleSendNotification = async () => {
    const title = notificationTitle.trim();
    const message = notificationMessage.trim();

    if (!title || !message) {
      setNotificationFeedback({
        type: "error",
        message: "Agrega título y mensaje antes de enviar la notificación.",
      });
      return;
    }

    // Payload para la notificación
    const payload = {
      notification: {
        title,
        body: message,
      },
      data: {
        click_action: NOTIFICATION_DASHBOARD_URL,
        icon: NOTIFICATION_ICON_URL,
      },
    };

    try {
      setIsSendingNotification(true);
      setNotificationFeedback(null);
      // 1. Recolectar tokens
      const subscriptions =
        selectedUsersForNotification.length === 0
          ? notificationSubscriptions
          : notificationSubscriptions.filter((subscription) =>
              selectedUsersForNotification.includes(subscription.uid)
            );
      const tokens = [
        ...new Set(
          subscriptions
            .map((subscription) => subscription.token)
            .filter(Boolean)
        ),
      ];

      if (tokens.length === 0) {
        setNotificationFeedback({
          type: "warning",
          message:
            "No hay dispositivos registrados para los usuarios seleccionados.",
        });
        return;
      }

      // 2. Envía la notificación push
      const result = await sendPushNotification(payload, tokens);
      const successCount =
        result?.successCount ?? result?.response?.successCount ?? tokens.length;
      const failureCount =
        result?.failureCount ?? result?.response?.failureCount ?? 0;

      // 3. Guarda la notificación en Firestore
      await addDoc(collection(db, "notificaciones"), {
        title,
        message,
        image: "",
        type: "alert",
        video: "",
        createdAt: serverTimestamp(),
        recipients:
          selectedUsersForNotification.length === 0
            ? "Todos"
            : selectedUsersForNotification,
        tokenCount: tokens.length,
        successCount,
        failureCount,
        clickAction: NOTIFICATION_DASHBOARD_URL,
      });

      setNotificationFeedback({
        type: failureCount > 0 ? "warning" : "success",
        message: `Notificación enviada. Entregas exitosas: ${successCount}. Fallos: ${failureCount}.`,
      });
      // Resetea campos
      setNotificationTitle("");
      setNotificationMessage("");
      setSelectedUsersForNotification([]);
    } catch (error) {
      console.error("Error al enviar la notificación:", error);
      setNotificationFeedback({
        type: "error",
        message: "Hubo un problema al enviar la notificación.",
      });
    } finally {
      setIsSendingNotification(false);
    }
  };

  // ============ ACOMPAÑAMIENTO (POST) ====================
  // Crear un nuevo post
  const handleCreatePost = async (e) => {
    e.preventDefault();
    const title = postTitle.trim();
    const description = postDescription.trim();
    const header = postHeader.trim();
    const cta = postCTA.trim();
    const ctaLink = postCTALink.trim();

    if (!title || !description) {
      setPostFeedback({
        type: "error",
        message: "Ingresa al menos título y descripción.",
      });
      return;
    }

    if (header && !isValidUrl(header)) {
      setPostFeedback({
        type: "error",
        message: "La URL del header debe iniciar con http:// o https://.",
      });
      return;
    }

    if ((cta && !ctaLink) || (!cta && ctaLink)) {
      setPostFeedback({
        type: "warning",
        message: "Completa el texto del botón y su link, o deja ambos vacíos.",
      });
      return;
    }

    if (ctaLink && !isValidUrl(ctaLink)) {
      setPostFeedback({
        type: "error",
        message: "El link del botón debe iniciar con http:// o https://.",
      });
      return;
    }

    try {
      setIsCreatingPost(true);
      setPostFeedback(null);
      await addDoc(collection(db, "post"), {
        title,
        description,
        header, // imagen / video / URL
        cta,
        ctaLink,
        createdAt: serverTimestamp(),
      });
      setPostFeedback({
        type: "success",
        message: "Acompañamiento creado con éxito.",
      });

      // Reset
      setPostTitle("");
      setPostDescription("");
      setPostHeader("");
      setPostCTA("");
      setPostCTALink("");

      // Recargar posts
      await refreshPosts();
    } catch (error) {
      console.error("Error al crear post:", error);
      setPostFeedback({
        type: "error",
        message: "Hubo un error al crear el acompañamiento.",
      });
    } finally {
      setIsCreatingPost(false);
    }
  };

  // Eliminar post
  const handleDeletePost = async (postId) => {
    const shouldDelete = window.confirm(
      "¿Seguro que quieres eliminar este acompañamiento?"
    );

    if (!shouldDelete) return;

    try {
      setDeletingPostId(postId);
      setPostFeedback(null);
      await deleteDoc(doc(db, "post", postId));
      setPostFeedback({
        type: "success",
        message: "Acompañamiento eliminado con éxito.",
      });
      // Recargar la lista
      await refreshPosts();
    } catch (error) {
      console.error("Error al eliminar post:", error);
      setPostFeedback({
        type: "error",
        message: "Hubo un error al eliminar el acompañamiento.",
      });
    } finally {
      setDeletingPostId(null);
    }
  };

  // Render Acompañamiento
  const renderAcompanamiento = () => {
    const previewTitle = postTitle.trim() || "Título del acompañamiento";
    const previewDescription =
      postDescription.trim() ||
      "Aquí se verá la descripción que leerán los usuarios en su dashboard.";
    const previewHeader = postHeader.trim();

    return (
      <div className="acompanamiento-section">
        <div className="admin-page-header">
          <div>
            <span className="section-kicker">Acompañamiento</span>
            <h2>Crear contenido para el dashboard</h2>
            <p>
              Publica mensajes, recursos o llamados a la acción que aparecerán
              en la sección de acompañamiento de los usuarios.
            </p>
          </div>
          <div className="header-stat">
            <strong>{posts.length}</strong>
            <span>posts activos</span>
          </div>
        </div>

        {postFeedback && (
          <Alert
            className="admin-feedback"
            type={postFeedback.type}
            message={postFeedback.message}
            showIcon
          />
        )}

        <div className="acompanamiento-container">
          <section className="admin-card acompanamiento-form">
            <div className="card-heading">
              <div>
                <h3>Nuevo acompañamiento</h3>
                <p>
                  Mantén el mensaje breve, claro y con un CTA solo cuando haya
                  una acción concreta.
                </p>
              </div>
            </div>
            <form onSubmit={handleCreatePost}>
              <div className="form-group">
                <label>Título</label>
                <Input
                  value={postTitle}
                  onChange={(e) => setPostTitle(e.target.value)}
                  placeholder="Ej. Recordatorio de hidratación"
                  maxLength={90}
                  showCount
                />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <Input.TextArea
                  rows={6}
                  value={postDescription}
                  onChange={(e) => setPostDescription(e.target.value)}
                  placeholder="Escribe el mensaje principal para los usuarios."
                  maxLength={700}
                  showCount
                />
              </div>
              <div className="form-group">
                <label>Header (URL de imagen o video)</label>
                <Input
                  value={postHeader}
                  onChange={(e) => setPostHeader(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="post-cta-row">
                <div className="form-group">
                  <label>Texto del botón</label>
                  <Input
                    value={postCTA}
                    onChange={(e) => setPostCTA(e.target.value)}
                    placeholder="Ej. Ver recurso"
                    maxLength={34}
                  />
                </div>
                <div className="form-group">
                  <label>Link del botón</label>
                  <Input
                    value={postCTALink}
                    onChange={(e) => setPostCTALink(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <Button type="primary" htmlType="submit" loading={isCreatingPost}>
                {isCreatingPost ? "Publicando..." : "Publicar acompañamiento"}
              </Button>
            </form>
          </section>

          <aside className="admin-card post-preview-card">
            <span className="section-kicker">Vista previa</span>
            <h3>Así se verá el post</h3>
            <div className="post-card is-preview">
              {previewHeader && (
                <div className="post-media">
                  {isVideoUrl(previewHeader) ? (
                    <video src={previewHeader} controls />
                  ) : (
                    <img src={previewHeader} alt="Vista previa" />
                  )}
                </div>
              )}
              <div className="post-header">
                <div className="post-title">{previewTitle}</div>
                <div className="post-date">Vista previa</div>
              </div>
              <div className="post-body">{previewDescription}</div>
              {postCTA && postCTALink && (
                <div className="post-footer">
                  <Button type="default">{postCTA}</Button>
                </div>
              )}
            </div>
          </aside>
        </div>

        <section className="admin-card acompanamiento-posts">
          <div className="posts-heading">
            <div>
              <h3>Posts existentes</h3>
              <p>Revisa lo publicado y elimina contenido que ya no aplique.</p>
            </div>
          </div>
          {posts.length === 0 ? (
            <div className="empty-state">
              <strong>No hay acompañamientos creados todavía.</strong>
              <span>Publica el primero desde el formulario superior.</span>
            </div>
          ) : (
            <div className="posts-list">
              {posts.map((p) => (
                <article className="post-card" key={p.id}>
                  {p.header && (
                    <div className="post-media">
                      {isVideoUrl(p.header) ? (
                        <video src={p.header} controls />
                      ) : (
                        <img src={p.header} alt={p.title || "header"} />
                      )}
                    </div>
                  )}
                  <div className="post-content">
                    <div className="post-header">
                      <div className="post-title">{p.title}</div>
                      <div className="post-date">
                        {p.createdAt
                          ? moment(p.createdAt.toDate()).format(
                              "DD/MM/YYYY HH:mm"
                            )
                          : "Sin fecha"}
                      </div>
                    </div>
                    <div className="post-body">{p.description}</div>
                    <div className="post-footer">
                      {p.cta && p.ctaLink && (
                        <Button
                          type="default"
                          onClick={() => window.open(p.ctaLink, "_blank")}
                        >
                          {p.cta}
                        </Button>
                      )}
                      <Button
                        danger
                        loading={deletingPostId === p.id}
                        onClick={() => handleDeletePost(p.id)}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  };

  // RUTINAS - Renderiza tablas por día en dropdown
  const renderRoutineDropdowns = () => {
    const getVideoLabel = (videoUrl, index) => {
      const matchedVideo = videoOptions.find((video) => video.url === videoUrl);
      if (matchedVideo?.name) return matchedVideo.name;

      try {
        const pathname = new URL(videoUrl).pathname;
        const fileName = decodeURIComponent(pathname.split("/").pop() || "");
        return fileName || `Video ${index + 1}`;
      } catch {
        return `Video ${index + 1}`;
      }
    };

    return (
      <Collapse>
        {Object.keys(routineData).map((day) => {
          const exercises = Object.entries(routineData[day]).map(
            ([key, value], index) => ({
              key: index,
              ejercicio: key,
              descripcion: value.descripcion,
              series: value.nSet,
              repeticiones: value.nRepeticiones,
              peso: value.peso,
              descanso: value.descanso,
              videos: value.videos || [],
            })
          );

          const columns = [
            { title: "Ejercicio", dataIndex: "ejercicio", key: "ejercicio" },
            {
              title: "Descripción",
              dataIndex: "descripcion",
              key: "descripcion",
            },
            { title: "Series", dataIndex: "series", key: "series" },
            {
              title: "Repeticiones",
              dataIndex: "repeticiones",
              key: "repeticiones",
            },
            { title: "Peso", dataIndex: "peso", key: "peso" },
            { title: "Descanso", dataIndex: "descanso", key: "descanso" },
            {
              title: "Videos",
              dataIndex: "videos",
              key: "videos",
              render: (videos) =>
                videos?.length ? (
                  <div className="assigned-video-pills">
                    {videos.map((videoUrl, videoIndex) => (
                      <span key={videoUrl} className="assigned-video-pill">
                        {getVideoLabel(videoUrl, videoIndex)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="empty-video-pill">Sin videos</span>
                ),
            },
          ];

          return (
            <Panel
              header={routineDayLabels[day] || day.charAt(0).toUpperCase() + day.slice(1)}
              key={day}
            >
              <Table
                dataSource={exercises}
                columns={columns}
                pagination={false}
                size="small"
                className="routine-table"
                scroll={{ x: 820 }}
              />
            </Panel>
          );
        })}
      </Collapse>
    );
  };

  // Render principal según `view`
  const renderContent = () => {
    const usersWithHistory = users.filter((user) =>
      clinicalHistories.some((history) => history.id === user.id)
    );
    const usersWithoutHistory = users.length - usersWithHistory.length;

    if (view === "acompanamiento") {
      return renderAcompanamiento();
    }

    if (view === "notificaciones") {
      const selectedRecipientCount = selectedUsersForNotification.length;
      const targetSubscriptions =
        selectedRecipientCount === 0
          ? notificationSubscriptions
          : notificationSubscriptions.filter((subscription) =>
              selectedUsersForNotification.includes(subscription.uid)
            );
      const targetTokenCount = new Set(
        targetSubscriptions
          .map((subscription) => subscription.token)
          .filter(Boolean)
      ).size;
      const usersWithToken = new Set(
        notificationSubscriptions
          .filter((subscription) => subscription.token)
          .map((subscription) => subscription.uid)
      );

      return (
        <div className="notifications-section">
          <div className="admin-page-header">
            <div>
              <span className="section-kicker">Notificaciones</span>
              <h2>Enviar push a usuarios</h2>
              <p>
                Redacta un mensaje breve, elige destinatarios específicos o
                deja el selector vacío para enviar a todos los dispositivos
                registrados.
              </p>
            </div>
            <div className="header-stat">
              <strong>{targetTokenCount}</strong>
              <span>dispositivos objetivo</span>
            </div>
          </div>

          {notificationFeedback && (
            <Alert
              className="admin-feedback"
              type={notificationFeedback.type}
              message={notificationFeedback.message}
              showIcon
            />
          )}

          <div className="notifications-grid">
            <section className="admin-card notification-composer">
              <div className="card-heading">
                <div>
                  <h3>Mensaje</h3>
                  <p>
                    El push abrirá directamente el dashboard del usuario en
                    Mentfull.
                  </p>
                </div>
              </div>

              <div className="form-group">
                <label>Destinatarios</label>
                <Select
                  className="notification-recipient-select"
                  mode="multiple"
                  allowClear
                  placeholder="Deja vacío para enviar a todos"
                  value={selectedUsersForNotification}
                  onChange={(value) => setSelectedUsersForNotification(value)}
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                >
                  {users.map((user) => {
                    const label = `${user.name || "Usuario"} - ${
                      user.email || "sin correo"
                    }`;
                    return (
                      <Select.Option
                        key={user.id}
                        value={user.id}
                        label={label}
                      >
                        <span>{label}</span>
                        {!usersWithToken.has(user.id) && (
                          <span className="token-warning"> sin token</span>
                        )}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>

              <div className="form-group">
                <label>Título</label>
                <Input
                  placeholder="Ej. Tu rutina de hoy está lista"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  maxLength={70}
                  showCount
                />
              </div>

              <div className="form-group">
                <label>Mensaje</label>
                <Input.TextArea
                  rows={5}
                  placeholder="Escribe un mensaje claro y accionable."
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  maxLength={220}
                  showCount
                />
              </div>

              <div className="notification-actions">
                <Button
                  type="primary"
                  onClick={handleSendNotification}
                  loading={isSendingNotification}
                >
                  {isSendingNotification
                    ? "Enviando..."
                    : "Enviar notificación"}
                </Button>
                <Button
                  onClick={() => {
                    setNotificationTitle("");
                    setNotificationMessage("");
                    setSelectedUsersForNotification([]);
                    setNotificationFeedback(null);
                  }}
                >
                  Limpiar
                </Button>
              </div>
            </section>

            <aside className="admin-card notification-preview-card">
              <span className="section-kicker">Resumen</span>
              <h3>Vista previa del push</h3>
              <div className="push-preview">
                <img src="/mentfull_ico.png" alt="Mentfull" />
                <div>
                  <strong>
                    {notificationTitle.trim() || "Título de la notificación"}
                  </strong>
                  <p>
                    {notificationMessage.trim() ||
                      "El mensaje aparecerá aquí antes de enviarse."}
                  </p>
                </div>
              </div>
              <div className="notification-summary">
                <div>
                  <span>Usuarios seleccionados</span>
                  <strong>
                    {selectedRecipientCount === 0
                      ? "Todos"
                      : selectedRecipientCount}
                  </strong>
                </div>
                <div>
                  <span>Tokens disponibles</span>
                  <strong>{targetTokenCount}</strong>
                </div>
                <div>
                  <span>Destino al tocar</span>
                  <strong>Dashboard</strong>
                </div>
              </div>
              <p className="helper-note">
                Si un usuario aparece “sin token”, debe iniciar sesión y
                aceptar notificaciones desde su navegador.
              </p>
            </aside>
          </div>
        </div>
      );
    }

    if (view === "dietas") {
      return (
        <div className="admin-diet-view">
          <div className="admin-page-header">
            <div>
              <span className="section-kicker">Dietas</span>
              <h2>Enviar plan alimenticio</h2>
              <p>
                Selecciona al usuario, adjunta el PDF y confirma la carga. El
                archivo quedará disponible en su dashboard.
              </p>
            </div>
            <div className="header-stat">
              <strong>{users.length}</strong>
              <span>usuarios registrados</span>
            </div>
          </div>

          <div className="diet-layout">
            <form className="admin-card diet-form" onSubmit={handleDietSubmit}>
              <div className="card-heading">
                <div>
                  <h3>Nueva dieta</h3>
                  <p>
                    Usa archivos PDF con nombres descriptivos para encontrarlos
                    fácilmente después.
                  </p>
                </div>
              </div>
              <div className="form-group">
                <label>Usuario</label>
                <Select
                  value={selectedUser || undefined}
                  onChange={(value) => setSelectedUser(value)}
                  style={{ width: "100%" }}
                  placeholder="Seleccione un usuario"
                  showSearch
                  optionFilterProp="children"
                >
                  {users.map((user) => (
                    <Select.Option key={user.id} value={user.id}>
                      {user.name} - {user.email}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="form-group">
                <label>Archivo PDF</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  required
                />
                {pdfFile && (
                  <span className="selected-file">{pdfFile.name}</span>
                )}
              </div>
              <Button type="primary" htmlType="submit">
                Enviar dieta
              </Button>
            </form>

            <aside className="admin-card diet-helper-card">
              <span className="section-kicker">Checklist</span>
              <h3>Antes de enviar</h3>
              <ul>
                <li>Confirma que elegiste al usuario correcto.</li>
                <li>Verifica que el archivo sea PDF y esté actualizado.</li>
                <li>Después de subirlo, revisa el dashboard del usuario si hay dudas.</li>
              </ul>
            </aside>
          </div>
        </div>
      );
    }

    if (view === "rutinas") {
      return (
        <div className="admin-routines-view">
          <div className="admin-page-header">
            <div>
              <span className="section-kicker">Rutinas</span>
              <h2>Asignar rutina al usuario</h2>
              <p>
                Elige un usuario, arma la rutina por día o carga una rutina
                predeterminada. Al final presiona Guardar Rutina para vincularla
                al usuario seleccionado.
              </p>
            </div>
            <div className="header-stat">
              <strong>{videoOptions.length}</strong>
              <span>videos disponibles</span>
            </div>
          </div>

          <div className="admin-card user-picker-card">
            <label>Usuario que recibirá la rutina</label>
            <Select
              value={selectedUser || undefined}
              onChange={(value) => setSelectedUser(value)}
              style={{ width: "100%" }}
              placeholder="Seleccione un usuario"
              showSearch
              optionFilterProp="children"
            >
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.name} - {user.email}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="admin-routine-grid">
            <section className="admin-card routine-builder-card">
              <div className="card-heading">
                <div>
                  <h3>Crear rutina manual</h3>
                  <p>
                    Agrega ejercicios al día correspondiente. Cada ejercicio
                    puede incluir uno o varios videos ya cargados.
                  </p>
                </div>
              </div>

              <div className="week-grid">
                {routineDays.map((day) => (
                  <Button key={day} onClick={() => showModal(day)}>
                    {routineDayLabels[day]}
                  </Button>
                ))}
              </div>

              <div className="routine-preview">
                {Object.keys(routineData).length > 0 ? (
                  renderRoutineDropdowns()
                ) : (
                  <div className="empty-state">
                    <strong>Aún no hay ejercicios agregados.</strong>
                    <span>
                      Usa los botones de días o selecciona una rutina
                      predeterminada para comenzar.
                    </span>
                  </div>
                )}
              </div>

              <div className="routine-actions">
                <Button
                  type="primary"
                  className="save-routine-button"
                  onClick={handleRoutineSubmit}
                >
                  Guardar Rutina
                </Button>
                <Button
                  className="save-preset-button"
                  onClick={showPresetNameModal}
                >
                  Guardar como rutina preestablecida
                </Button>
              </div>
            </section>

            <aside className="admin-routine-side">
              <section className="admin-card preset-routine-card">
                <span className="section-kicker">Atajo</span>
                <h3>Agregar rutina predeterminada</h3>
                <p>
                  Carga una rutina de la colección
                  <strong> rutinas_preestablecidas</strong>. Selecciónala,
                  revisa los días cargados y guárdala para vincularla al
                  usuario seleccionado.
                </p>
                <Select
                  value={selectedPredefinedRoutine || undefined}
                  onChange={(value) => setSelectedPredefinedRoutine(value)}
                  style={{ width: "100%" }}
                  placeholder={
                    predefinedRoutines.length
                      ? "Selecciona una rutina"
                      : "No hay rutinas creadas todavía"
                  }
                  disabled={predefinedRoutines.length === 0}
                >
                  {predefinedRoutines.map((routine) => (
                    <Select.Option key={routine.id} value={routine.id}>
                      {routine.nombre || routine.name || routine.id}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  ghost
                  onClick={handleApplyPredefinedRoutine}
                  disabled={predefinedRoutines.length === 0}
                >
                  Agregar rutina predeterminada
                </Button>
              </section>

              <section className="admin-card video-upload-card">
                <span className="section-kicker">Biblioteca</span>
                <h3>Subir video de ejercicio</h3>
                <p>
                  Sube videos al repositorio de Firebase Storage. Al terminar,
                  aparecerán en el selector de videos de cada ejercicio.
                </p>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  onChange={(e) => {
                    setExerciseVideoFile(e.target.files[0] || null);
                    setVideoUploadProgress(0);
                  }}
                />
                {exerciseVideoFile && (
                  <span className="selected-file">
                    {exerciseVideoFile.name}
                  </span>
                )}
                {isUploadingVideo && (
                  <div className="upload-progress">
                    <div style={{ width: `${videoUploadProgress}%` }} />
                    <span>{videoUploadProgress}%</span>
                  </div>
                )}
                <Button
                  type="primary"
                  onClick={handleExerciseVideoUpload}
                  disabled={!exerciseVideoFile || isUploadingVideo}
                >
                  {isUploadingVideo ? "Subiendo..." : "Subir video"}
                </Button>
              </section>
            </aside>
          </div>

          <Modal
            title="Guardar rutina preestablecida"
            open={isPresetNameModalVisible}
            onOk={handleSavePredefinedRoutine}
            onCancel={() => setIsPresetNameModalVisible(false)}
            okText="Guardar"
            cancelText="Cancelar"
            confirmLoading={isSavingPresetRoutine}
          >
            <p>
              Escribe el nombre de la rutina. Ese nombre se usará como ID del
              documento en <strong>rutinas_preestablecidas</strong>.
            </p>
            <Input
              value={presetRoutineName}
              onChange={(e) => setPresetRoutineName(e.target.value)}
              placeholder="Ej. Principiante fuerza 3 días"
              onPressEnter={handleSavePredefinedRoutine}
            />
          </Modal>

          <Modal
            title={`Agregar ejercicios para ${selectedDay}`}
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
          >
            <Form form={exerciseForm} layout="vertical">
              <Form.Item
                name="descripcion"
                label="Descripción"
                rules={[{ required: true }]}
              >
                <Input.TextArea />
              </Form.Item>
              <Form.Item
                name="nSet"
                label="Número de Series"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="nRepeticiones"
                label="Repeticiones"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="peso"
                label="Peso (lbs)"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="descanso"
                label="Descanso (segundos)"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="videos" label="Videos">
                <Select mode="multiple" placeholder="Seleccione videos">
                  {videoOptions.map((video) => (
                    <Select.Option key={video.url} value={video.url}>
                      {video.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      );
    }

    // Vista "home" o "historiales"
    return (
      <div className="admin-home-view">
        <div className="admin-page-header">
          <div>
            <span className="section-kicker">
              {view === "historiales" ? "Historiales" : "Inicio"}
            </span>
            <h2>Usuarios registrados</h2>
            <p>
              Consulta usuarios, identifica quién ya envió historial clínico y
              descarga la información cuando necesites revisarla.
            </p>
          </div>
          <div className="header-stat">
            <strong>{users.length}</strong>
            <span>usuarios totales</span>
          </div>
        </div>

        <div className="admin-summary-grid">
          <section className="admin-card admin-summary-card">
            <span>Con historial</span>
            <strong>{usersWithHistory.length}</strong>
          </section>
          <section className="admin-card admin-summary-card">
            <span>Pendientes</span>
            <strong>{usersWithoutHistory}</strong>
          </section>
          <section className="admin-card admin-summary-card">
            <span>Videos de rutina</span>
            <strong>{videoOptions.length}</strong>
          </section>
        </div>

        <section className="admin-card users-table-card">
          <div className="card-heading">
            <div>
              <h3>Directorio de usuarios</h3>
              <p>
                Usa la búsqueda del navegador o el orden de columnas para ubicar
                a una persona rápidamente.
              </p>
            </div>
          </div>
          <Table
            dataSource={users.map((user) => ({
              key: user.id,
              name: user.name || "Sin nombre",
              email: user.email || "Sin correo",
              phone: user.phone || "Sin teléfono",
              plan: user.subscriptionPlan || "Sin plan",
              hasHistory: clinicalHistories.some(
                (history) => history.id === user.id
              ),
            }))}
            columns={[
              { title: "Nombre", dataIndex: "name", key: "name" },
              { title: "Email", dataIndex: "email", key: "email" },
              { title: "Teléfono", dataIndex: "phone", key: "phone" },
              { title: "Plan", dataIndex: "plan", key: "plan" },
              {
                title: "Historial clínico",
                dataIndex: "hasHistory",
                key: "hasHistory",
                render: (hasHistory, user) =>
                  hasHistory ? (
                    <Button
                      onClick={() =>
                        generatePDF(
                          user,
                          clinicalHistories.find(
                            (history) => history.id === user.key
                          )
                        )
                      }
                    >
                      Descargar historial
                    </Button>
                  ) : (
                    <span className="status-pill status-pill--pending">
                      Pendiente
                    </span>
                  ),
              },
            ]}
            pagination={{ pageSize: 8 }}
            scroll={{ x: 820 }}
          />
        </section>
      </div>
    );
  };

  return (
    <div className="dashboard-admin-container">
      <NavbarDA setView={setView} activeView={view} />
      <div className="main-content">
        {loading ? (
          <div className="loader-container">
            <Loader />
          </div>
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

// Detectar si es un video (extensión mp4, mov, etc.)
function isVideoUrl(url) {
  if (!url) return false;
  const ext = url.split("?")[0].split(".").pop().toLowerCase();
  return ["mp4", "mov", "webm"].includes(ext);
}

function isValidUrl(url) {
  try {
    const parsedUrl = new URL(url);
    return ["http:", "https:"].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
}

function normalizePredefinedRoutine(routine) {
  if (!routine) return {};

  const source = routine.plan || routine.rutina || routine;
  return routineDays.reduce((acc, day) => {
    const aliases = routineDayAliases[day] || [day];
    const matchedKey = aliases.find((key) => source[key]);
    if (matchedKey) {
      acc[day] = source[matchedKey];
    }
    return acc;
  }, {});
}

export default DashboardAdmin;
